/**
 * Swamithra — attachments and the signed URLs that reach them.
 *
 * NO ATTACHMENT HAS A URL IN ITS PAYLOAD. The record carries an id, a name, a
 * content type and a size; a URL is minted per open, with a 900-second TTL, from
 * a separate call (US-00-002.18, US-00-006.5). That is deliberate: a URL in the
 * list payload would be logged, cached, and still live in a screenshot an hour
 * later.
 */

import { z } from "zod"

import { SIGNED_URL_TTL_SECONDS } from "./constants"

/**
 * Which slot an attachment occupies.
 *
 * Vetting requests carry exactly one notice and exactly one draft, and the
 * accepted types differ between them (US-00-004.3), so the slot has to travel
 * with the record rather than being inferred from position in an array.
 */
export const attachmentRoleSchema = z.enum([
	"query",
	"message",
	"vetting_notice",
	"vetting_draft",
	// A file on an Expert's unsent response draft. Panel only; on send it
	// becomes a "message" file.
	"response_draft",
])
export type AttachmentRole = z.infer<typeof attachmentRoleSchema>

export const attachmentSchema = z.object({
	id: z.string(),
	fileName: z.string(),
	/** As sniffed by the server, not as claimed by the upload (US-00-002.2). */
	contentType: z.string(),
	sizeBytes: z.number().int().nonnegative(),
	role: attachmentRoleSchema,
	/**
	 * OPTIONAL, because the service does not send it. The attachment payload is
	 * id, name, sniffed type, size and slot and nothing else; a required
	 * `uploadedAt` here failed the parse of every query detail that had a file
	 * on it. A thread already orders itself by its messages' `createdAt`.
	 */
	uploadedAt: z.string().optional(),
})

export type Attachment = z.infer<typeof attachmentSchema>

/**
 * A minted download link.
 *
 * The URL is the only field the service is required to send. `expiresAt` and
 * `ttlSeconds` are filled from whatever it did send — a unix `expires`, or the
 * agreed 900 seconds — because a client that failed to parse a perfectly good
 * signed URL over a missing expiry field would refuse to open a file it had been
 * handed (US-00-002.18, US-00-006.5/.6).
 */
export const signedUrlSchema = z.preprocess(
	value => {
		if (value === null || typeof value !== "object") return value
		const record = value as Record<string, unknown>
		const expiresAt =
			record.expiresAt ??
			(typeof record.expires === "number"
				? new Date(record.expires * 1000).toISOString()
				: undefined)
		return {
			...record,
			expiresAt:
				expiresAt ?? new Date(Date.now() + SIGNED_URL_TTL_SECONDS * 1000).toISOString(),
			ttlSeconds: record.ttlSeconds ?? SIGNED_URL_TTL_SECONDS,
		}
	},
	z.object({
		url: z.string().url(),
		/** RFC 3339. The client refetches rather than reasoning about the clock. */
		expiresAt: z.string(),
		ttlSeconds: z.number().int().positive(),
	}),
)

export type SignedUrl = z.output<typeof signedUrlSchema>

/**
 * True when a minted URL is close enough to expiry that opening it is a race.
 *
 * A URL is minted per open, so this only matters for the case the criteria call
 * out explicitly — a user who left the detail screen open and came back
 * (US-00-006.6). The margin is a tenth of the TTL: long enough to survive a slow
 * PDF handshake, short enough that a re-mint is rare.
 */
export function isSignedUrlStale(signed: SignedUrl, now = new Date()): boolean {
	const expiry = new Date(signed.expiresAt).getTime()
	if (Number.isNaN(expiry)) return true
	return expiry - now.getTime() <= (SIGNED_URL_TTL_SECONDS / 10) * 1000
}

/**
 * How a client should open an attachment.
 *
 * The criteria specify different behaviour per platform and per type — inline
 * preview for images, a new tab for PDFs on web, the device viewer on mobile,
 * download-only for DOCX (US-00-002.17, US-00-004.13/.14, US-01-004.7). The
 * decision is shared; the doing is not.
 */
export type AttachmentPresentation = "image" | "pdf" | "download_only"

export function presentationFor(contentType: string, fileName = ""): AttachmentPresentation {
	const type = contentType.toLowerCase()
	if (type.startsWith("image/")) return "image"
	if (type === "application/pdf") return "pdf"
	// A server that lost the content type still leaves the extension, and a PDF
	// that downloads instead of opening is a worse failure than a wrong guess.
	const lower = fileName.toLowerCase()
	if (lower.endsWith(".pdf")) return "pdf"
	if (/\.(jpe?g|png)$/.test(lower)) return "image"
	return "download_only"
}

/**
 * What to call a presentation on screen.
 *
 * The counterpart to `presentationFor`, and it lives here for the same reason:
 * both applications' attachment lists had their own identical copy, and the
 * attachment viewer had neither — so that screen printed the raw MIME type
 * ("application/pdf") to the reader, twice, while the rows two clicks away said
 * "PDF". A label the product shows a person is a product decision, not a
 * component's.
 */
export function labelForPresentation(presentation: AttachmentPresentation): string {
	switch (presentation) {
		case "image":
			return "Image"
		case "pdf":
			return "PDF"
		case "download_only":
			return "Document"
	}
}
